<script lang="ts">
    import { onMount, type ComponentProps } from 'svelte';
    import { createPopper, type Rect } from '@popperjs/core';
    import { twMerge } from 'tailwind-merge';
    import type { Placement, Instance } from '@popperjs/core';
    import createEventDispatcher from './createEventDispatcher';
    import Frame from './Frame.svelte';
    import { portal as portalAction } from 'svelte-portal';
    import Wrapper from './Wrapper.svelte';

    // propagate props type from underlying Frame
    interface $$Props extends ComponentProps<Frame> {
        activeContent?: boolean;
        arrow?: boolean;
        offset?: number;
        placement?: Placement;
        trigger?: 'hover' | 'click';
        triggeredBy?: string;
        reference?: string;
        strategy?: 'absolute' | 'fixed';
        open?: boolean;
        yOnly?: boolean;
        portal?: boolean;
    }

    export let activeContent = false;
    export let arrow = true;
    export let offset = 8;
    export let placement: Placement = 'top';
    export let trigger: 'hover' | 'click' = 'hover';
    export let triggeredBy: string | undefined = undefined;
    export let reference: string | undefined = undefined;
    export let strategy: 'absolute' | 'fixed' = 'absolute';
    export let open = false;
    export let yOnly = false;
    export let portal = false;

    const dispatch = createEventDispatcher();

    let clickable: boolean;
    $: clickable = trigger === 'click';

    $: dispatch('show', triggerEl, open);

    let referenceEl: Element | null = null;
    let triggerEl: Element;
    let contentEl: HTMLElement;
    let triggerEls: HTMLElement[] = [];
    let popper: Instance;

    let _blocked = false; // management of the race condition between focusin and click events
    const block = () => ((_blocked = true), setTimeout(() => (_blocked = false), 250));

    const showHandler = (ev: Event) => {
        if (triggerEl === undefined) console.error('trigger undefined');
        if (triggerEls.includes(ev.target as HTMLElement) && triggerEl !== ev.target) {
            triggerEl = ev.target as HTMLElement;
            block();
        }
        if (clickable && ev.type === 'focusin' && !open) block();
        open = clickable && ev.type === 'click' && !_blocked ? !open : true;
    };

    // reactivity
    $: popper && popper.setOptions({ placement });

    // typescript typeguards - poper.state.element.reference: Element|HTMLElement|VirtualElement
    const hasHover = (el: object) => (el as Element).matches && (el as Element).matches(':hover');
    const hasFocus = (el: object) => (el as Element).contains && (el as Element).contains(document.activeElement);

    const hideHandler = (ev: Event) => {
        if (activeContent) {
            setTimeout(() => {
                const elements = Object.values(popper?.state.elements ?? {});
                if (ev.type === 'mouseleave' && elements.some(hasHover)) return;
                if (ev.type === 'focusout' && elements.some(hasFocus)) return;
                open = false;
            }, 100);
        } else open = false;
    };

    function init(node: HTMLElement, _referenceEl: Element) {
        const arrowEl = arrow ? node.lastElementChild : undefined;
        popper = createPopper(_referenceEl, node, {
            placement,
            strategy,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: ({ reference, popper }: { reference: Rect; popper: Rect }) => {
                            // for full screen mega menu
                            return [yOnly ? popper.width / 2 - reference.width / 2 - reference.x : 0, offset];
                        },
                    },
                },
                { name: 'eventListeners', enabled: open },
                { name: 'flip', enabled: false },
                { name: 'arrow', enabled: arrow, options: { element: arrowEl, padding: 10 } },
            ],
        });
        return {
            update(_referenceEl: Element) {
                popper.state.elements.reference = _referenceEl;
                popper.update();
            },
            destroy() {
                popper.destroy();
            },
        };
    }

    onMount(() => {
        const events: [string, any, boolean][] = [
            ['focusin', showHandler, true],
            ['focusout', hideHandler, true],
            ['click', showHandler, clickable],
            ['mouseenter', showHandler, !clickable],
            ['mouseleave', hideHandler, !clickable],
        ];

        if (reference) {
            referenceEl = document.querySelector(reference);
            if (!referenceEl) {
                console.error(`Reference element not found: ${reference}`);
                return;
            }
        }

        if (referenceEl instanceof HTMLElement) {
            triggerEls = [referenceEl];
        } else if (triggeredBy) {
            triggerEls = Array.from(document.querySelectorAll<HTMLElement>(triggeredBy));
        } else {
            triggerEls = contentEl?.previousElementSibling ? [contentEl.previousElementSibling as HTMLElement] : [];
        }

        if (!triggerEls.length) {
            console.error('No triggers found.');
            return;
        }

        triggerEls.forEach((element: HTMLElement) => {
            if (element.tabIndex < 0) element.tabIndex = 0; // trigger must be focusable
            for (const [name, handler, cond] of events) if (cond) element.addEventListener(name, handler);
        });

        const nextTriggerEl = triggerEls[0];
        if (!nextTriggerEl) {
            console.error('No primary trigger found.');
            return;
        }

        triggerEl = nextTriggerEl;

        if (!referenceEl) {
            referenceEl = triggerEl;
        }

        return () => {
            triggerEls.forEach((element: HTMLElement) => {
                if (element) {
                    for (const [name, handler] of events) element.removeEventListener(name, handler);
                }
            });
        };
    });
    function optional(pred: boolean, func: (ev: Event) => void) {
        return (pred && func) || null;
    }

    let position = 'bottom';
    $: position = placement.split('-', 1)[0];

    let arrowClass: string;
    $: arrowClass = twMerge(
        'absolute h-[9px] w-[9px] before:absolute before:inset-0 before:rotate-45 before:bg-[var(--popper-arrow-background,inherit)] before:border-inherit before:content-[""]',
        position === 'top' && ($$props.border ? 'before:border-b before:border-r -bottom-[5px]' : '-bottom-[4px]'),
        position === 'bottom' && ($$props.border ? 'before:border-t before:border-l -top-[5px]' : '-top-[4px]'),
        position === 'left' && ($$props.border ? 'before:border-t before:border-r -right-[5px]' : '-right-[4px]'),
        position === 'right' && ($$props.border ? 'before:border-b before:border-l -left-[5px]' : '-left-[4px]'),
    );
</script>

{#if !reference && !triggeredBy}
    <!-- Keep the placeholder mounted when the previous sibling is used as the trigger. -->
    <div bind:this={contentEl} style="display: none;" />
{/if}

{#if open && referenceEl}
    <Wrapper tag="div" show={portal} use={portalAction}>
        <Frame
            use={init}
            options={referenceEl}
            role="tooltip"
            tabindex={activeContent ? -1 : undefined}
            on:focusin={optional(activeContent, showHandler)}
            on:focusout={optional(activeContent, hideHandler)}
            on:mouseenter={optional(activeContent && !clickable, showHandler)}
            on:mouseleave={optional(activeContent && !clickable, hideHandler)}
            {...$$restProps}
            class={twMerge('outline-none', $$props.class)}
        >
            <slot />
            {#if arrow}<div class={arrowClass} />{/if}
        </Frame>
    </Wrapper>
{/if}

<!--
  @component
  ## Props
  @prop activeContent: boolean = false;
  @prop arrow: boolean = true;
  @prop offset: number = 8;
  @prop placement: Placement = 'top';
  @prop trigger: 'hover' | 'click' = 'hover';
  @prop triggeredBy: string | undefined = undefined;
  @prop reference: string | undefined = undefined;
  @prop strategy: 'absolute' | 'fixed' = 'absolute';
  @prop open: boolean = false;
  @prop yOnly: boolean = false;
-->
